import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, Trash2, Upload } from "lucide-react";

import { adminApi } from "@/lib/api";
import { AdminInput, AdminSelect, ConfirmDialog, EmptyState, FieldLabel, Modal } from "@/components/admin/AdminUI";

const CATEGORIES = ["logo", "residential", "bim", "team", "general"];
const MAX_SIZE = 5 * 1024 * 1024;

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [openEdit, setOpenEdit] = useState(null);

  const load = () => {
    const params = {};
    if (filter) params.category = filter;
    if (q) params.q = q;
    adminApi.listMedia(params).then(setItems);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, q]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_SIZE) {
      toast.error("File too large (max 5MB).");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await adminApi.createMedia({
          filename: file.name,
          url: reader.result,
          mime_type: file.type || "application/octet-stream",
          size: file.size,
          category: filter || "general",
        });
        toast.success("Uploaded");
        load();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const saveMeta = async (e) => {
    e.preventDefault();
    if (!openEdit) return;
    try {
      await adminApi.updateMedia(openEdit.id, {
        alt: openEdit.alt,
        caption: openEdit.caption,
        category: openEdit.category,
      });
      toast.success("Saved");
      setOpenEdit(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try { await adminApi.deleteMedia(confirmDelete.id); toast.success("Deleted"); load(); setConfirmDelete(null); } catch { toast.error("Delete failed"); }
  };

  const copyUrl = (url) => {
    try { navigator.clipboard?.writeText(url); toast.success("URL copied to clipboard"); } catch { /* ignore */ }
  };

  return (
    <div className="space-y-6">
      <div className="admin-card rounded-sm p-4 md:p-5 grid md:grid-cols-4 gap-3 items-end">
        <label>
          <FieldLabel>Category</FieldLabel>
          <AdminSelect value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">All</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </AdminSelect>
        </label>
        <label className="md:col-span-2">
          <FieldLabel>Search</FieldLabel>
          <AdminInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filename, alt or caption" />
        </label>
        <label className="btn-primary justify-center cursor-pointer">
          <Upload className="h-4 w-4" /> Upload image
          <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="media-upload" />
        </label>
      </div>

      {items.length === 0 ? (
        <EmptyState title="No media yet" description="Upload images to reuse them in projects, hero backgrounds and more." />
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((m) => (
            <div key={m.id} className="admin-card rounded-sm overflow-hidden">
              <div className="aspect-square bg-ink-graphite overflow-hidden">
                <img src={m.url} alt={m.alt || m.filename} className="h-full w-full object-cover" />
              </div>
              <div className="p-3">
                <p className="text-xs truncate" title={m.filename}>{m.filename}</p>
                <p className="text-[10px] uppercase tracking-widest text-brand-gray mt-1">
                  {m.category} • {Math.round((m.size || 0) / 1024)} KB
                </p>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <button type="button" onClick={() => setOpenEdit({ ...m })} className="btn-outline text-[10px] px-2.5 py-1.5">Edit</button>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => copyUrl(m.url)} className="h-8 w-8 grid place-items-center border border-white/10 rounded-sm text-brand-gray hover:text-brand-yellow"><Copy className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => setConfirmDelete(m)} className="h-8 w-8 grid place-items-center border border-white/10 rounded-sm text-brand-gray hover:text-brand-red"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!openEdit} onClose={() => setOpenEdit(null)} title="Edit media">
        {openEdit && (
          <form onSubmit={saveMeta} className="space-y-4">
            <div className="aspect-video bg-ink-graphite rounded-sm overflow-hidden">
              <img src={openEdit.url} alt="" className="h-full w-full object-contain" />
            </div>
            <label><FieldLabel>Alt text</FieldLabel><AdminInput value={openEdit.alt || ""} onChange={(e) => setOpenEdit({ ...openEdit, alt: e.target.value })} /></label>
            <label><FieldLabel>Caption</FieldLabel><AdminInput value={openEdit.caption || ""} onChange={(e) => setOpenEdit({ ...openEdit, caption: e.target.value })} /></label>
            <label><FieldLabel>Category</FieldLabel>
              <AdminSelect value={openEdit.category} onChange={(e) => setOpenEdit({ ...openEdit, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </AdminSelect>
            </label>
            <div className="flex justify-end gap-3">
              <button type="button" className="btn-outline" onClick={() => setOpenEdit(null)}>Cancel</button>
              <button type="submit" className="btn-primary">Save</button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog open={!!confirmDelete} onCancel={() => setConfirmDelete(null)} onConfirm={doDelete} title="Delete media" description={`Delete '${confirmDelete?.filename}'?`} />
    </div>
  );
}
